/**
 * useServerFreshness (AVM-216 audit row 23, 2026-07-12) — closes the loop the
 * doorbell model left open: real-time/scheduled syncs run SERVER-side, so an
 * open page never saw the new data ("changes arrive in seconds" stopped at the
 * database). This hook polls /api/sync-settings (3 cheap reads) while the tab
 * is visible and fires the existing `jira-synced` window event whenever any
 * source's lastSynced advances — every surface already listening refreshes,
 * no new reactivity system. Deliberately NOT websockets/SSE: a 45s poll is
 * honest, cheap, and proportionate until real-time load justifies a push
 * channel (tracked with AVM-221's remaining items).
 */
import { useEffect, useRef } from 'react';
import { syncSettingsApi } from './api';

const POLL_MS = 45_000;

export function useServerFreshness(): void {
  const lastSeen = useRef<string>('');

  useEffect(() => {
    let stop = false;
    const check = async () => {
      if (stop || document.visibilityState !== 'visible') return;
      try {
        const r = await syncSettingsApi.get();
        const stamp = (r.data.sources as Array<{ lastSynced: string | null }>)
          .map(s => s.lastSynced ?? '')
          .join('|');
        if (lastSeen.current && stamp !== lastSeen.current) {
          window.dispatchEvent(new CustomEvent('jira-synced')); // shared data-refresh signal
        }
        lastSeen.current = stamp;
      } catch { /* transient — next tick retries */ }
    };
    check();
    const timer = window.setInterval(check, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stop = true; window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, []);
}
